import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BiAward, BiBriefcase, BiFileBlank } from 'react-icons/bi';
import { parivartanAPI } from '../../../services/parivartanAPI';
import WorkspaceDataTable from '../../../components/workspace/WorkspaceDataTable';
import WorkspacePageHeader from '../../../components/workspace/WorkspacePageHeader';
import { useAdminRecordDelete } from '../../../components/workspace/useAdminRecordDelete';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { formatWorkspaceDate } from '../../../utils/workspaceDates';
import './KnowledgeBankPage.scss';

const SECTIONS = [
  { id: 'credentials', label: 'Credentials', newLabel: 'New credential', icon: BiAward },
  { id: 'projects', label: 'Project experience', newLabel: 'New project', icon: BiBriefcase },
  { id: 'reusable', label: 'Reusable content', newLabel: 'New content', icon: BiFileBlank }
];

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return formatWorkspaceDate(date);
};

const titleCase = (value) => String(value || 'draft').replace(/_/g, ' ');

const ExpiryBadge = ({ status }) => {
  const key = status || 'unknown';
  return <span className={`kb-page__badge kb-page__badge--${key}`}>{titleCase(key === 'unknown' ? 'No expiry' : key)}</span>;
};

const StatusBadge = ({ status }) => {
  const key = status || 'draft';
  return <span className={`kb-page__badge kb-page__badge--${key}`}>{titleCase(key)}</span>;
};

const KnowledgeBankPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('section');
  const section = SECTIONS.some((item) => item.id === requested) ? requested : 'credentials';
  const current = SECTIONS.find((item) => item.id === section);
  const [credentials, setCredentials] = useState([]);
  const [projects, setProjects] = useState([]);
  const [reusable, setReusable] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useDocumentTitle('Knowledge bank');

  const load = useCallback(async () => {
    setError('');
    try {
      const [library, linkRes] = await Promise.all([
        parivartanAPI.listKnowledge(),
        parivartanAPI.listTenderCredentials().catch(() => ({ data: { data: { links: [] } } }))
      ]);
      const data = library.data?.data || {};
      setCredentials(data.credentials || []);
      setProjects(data.projectExperience || []);
      setReusable(data.reusableContent || []);
      setLinks(linkRes.data?.data?.links || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load the knowledge bank');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const knowledgeEntity = section === 'projects' ? 'project' : section === 'reusable' ? 'reusable' : 'credential';
  const admin = useAdminRecordDelete({
    entity: knowledgeEntity,
    noun: section === 'projects' ? 'project' : section === 'reusable' ? 'content item' : 'credential',
    plural: section === 'projects' ? 'projects' : section === 'reusable' ? 'content items' : 'credentials',
    reload: load,
    label: (row) => row.projectName || row.title || 'Record'
  });

  const credentialRows = useMemo(
    () => credentials.map((item) => ({
      ...item,
      expiryLabel: formatDate(item.validTo),
      daysLabel: item.daysToExpiry != null && item.expiryStatus !== 'unknown' ? `${item.daysToExpiry} days` : '—',
      onTendersLabel: String(links.filter((link) => String(link.credentialId) === String(item._id)).length || '—')
    })),
    [credentials, links]
  );

  const projectRows = useMemo(
    () => projects.map((item) => ({
      ...item,
      completedLabel: formatDate(item.completionDate),
      valueLabel: item.valueInr ? `₹${(item.valueInr / 1e7).toFixed(1)} Cr` : '—'
    })),
    [projects]
  );

  const reusableRows = useMemo(
    () => reusable.map((item) => ({
      ...item,
      snippet: (item.content || '').replace(/\s+/g, ' ').trim() || '—'
    })),
    [reusable]
  );

  const columns = useMemo(() => {
    if (section === 'projects') {
      return [
        { key: 'projectName', label: 'Project', mobileTitle: true },
        { key: 'client', label: 'Client' },
        { key: 'sector', label: 'Sector' },
        { key: 'serviceType', label: 'Service' },
        { key: 'valueLabel', label: 'Value', width: '120px' },
        { key: 'completedLabel', label: 'Completed', width: '140px' }
      ];
    }
    if (section === 'reusable') {
      return [
        { key: 'title', label: 'Title', mobileTitle: true },
        { key: 'classification', label: 'Type' },
        { key: 'version', label: 'Version', width: '100px' },
        {
          key: 'approvalStatus',
          label: 'Status',
          width: '130px',
          render: (value) => <StatusBadge status={value} />
        },
        {
          key: 'snippet',
          label: 'Preview',
          render: (value) => <span className="kb-page__reusable-snippet">{value}</span>
        }
      ];
    }
    return [
      {
        key: 'title',
        label: 'Credential',
        mobileTitle: true,
        render: (value, row) => (
          <div>
            <span className="kb-page__cred-title">{value}</span>
            {row.issuer ? <span className="kb-page__cred-meta">Issued by {row.issuer}</span> : null}
          </div>
        )
      },
      { key: 'category', label: 'Category' },
      { key: 'expiryLabel', label: 'Valid until', width: '140px' },
      { key: 'daysLabel', label: 'Remaining', width: '120px' },
      {
        key: 'expiryStatus',
        label: 'Expiry',
        width: '140px',
        render: (value) => <ExpiryBadge status={value} />
      },
      { key: 'onTendersLabel', label: 'On tenders', width: '120px' }
    ];
  }, [section]);

  const rows = section === 'projects' ? projectRows : section === 'reusable' ? reusableRows : credentialRows;

  const openRecord = (row, mode) => {
    const id = row._id || row.id;
    if (!id) return;
    navigate(mode === 'edit'
      ? `/apps/tender/knowledge/${section}/${id}/edit`
      : `/apps/tender/knowledge/${section}/${id}`);
  };

  const chooseSection = (id) => {
    setSearchParams(id === 'credentials' ? {} : { section: id }, { replace: true });
  };

  if (loading) return <Spinner animation="border" size="sm" />;

  return (
    <div className="kb-page">
      <WorkspacePageHeader
        title="Knowledge bank"
        help="knowledge"
        subtitle="The firm library. Open a record to update it. Credentials are attached to tenders from the credential page."
        actions={(
          <Button size="sm" variant="primary" onClick={() => navigate(`/apps/tender/knowledge/${section}/new`)}>
            {current.newLabel}
          </Button>
        )}
      />
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {admin.notice}
      <div className="kb-page__tabs" role="tablist" aria-label="Knowledge sections">
        {SECTIONS.map((item) => {
          const Icon = item.icon;
          const count = item.id === 'projects' ? projects.length : item.id === 'reusable' ? reusable.length : credentials.length;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={section === item.id}
              className={`kb-page__tab ${section === item.id ? 'is-active' : ''}`}
              onClick={() => chooseSection(item.id)}
            >
              <Icon size={18} aria-hidden />
              {item.label}
              <span className="kb-page__tab-count">{count}</span>
            </button>
          );
        })}
      </div>
      <WorkspaceDataTable
        downloadPermission="knowledge.download"
        title={current.label}
        data={rows}
        columns={columns}
        searchPlaceholder={
          section === 'projects'
            ? 'Search project, client, sector…'
            : section === 'reusable'
              ? 'Search title or type…'
              : 'Search title, category, issuer…'
        }
        pageSize={12}
        onView={(row) => openRecord(row, 'view')}
        onEdit={(row) => openRecord(row, 'edit')}
        {...admin.tableProps}
        rowActions={admin.wrapActions(() => [
          { type: 'view', label: 'View' },
          { type: 'edit', label: 'Edit' }
        ])}
      />
      {admin.modal}
    </div>
  );
};

export default KnowledgeBankPage;
